// The standalone cart template has only YMQ's reduced edit payload. Load the
// matching product environment in an isolated same-origin document and show
// only its complete native editor above /cart.
(() => {
  // Run in the top storefront document on every page. The isolated product
  // document inside the editor iframe must keep YMQ's native click path so it
  // can open that product's complete option payload without nesting editors.
  if (window.self !== window.top) return;

  let shell;
  let frame;
  let pollTimer;
  let timeoutTimer;
  let savePending = false;
  let activeEditorKey = "";
  let editorReady = false;
  let editorVisible = false;
  let revealRequested = false;
  const productHtmlCache = new Map();
  const escapePreviewHtml = (value) => String(value || "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  })[char]);

  const loadingPreview = (cartIndex, sourceItem) => {
    const item = sourceItem || document.querySelector(`.cart-item[data-cart-index="${CSS.escape(String(cartIndex || 0))}"]`);
    if (!item) return "";
    const title = item.querySelector(".cart-item__title")?.textContent?.trim() || "Your product";
    const image = item.querySelector(".cart-item__image img")?.currentSrc || item.querySelector(".cart-item__image img")?.src || "";
    const rows = [...item.querySelectorAll(".line-item-property, .cart-item__variant, .cart-item__property-summary span")]
      .map((row) => {
        const label = row.querySelector(".line-item-property__label, .cart-item__variant-label")?.textContent?.trim().replace(/:\s*$/, "") || "";
        const value = row.querySelector(".line-item-property__value, .cart-item__variant-value")?.textContent?.trim()
          || (!label ? row.textContent?.trim() : "");
        if (!value || /^_/.test(label)) return null;
        return { label: label || "Selected", value };
      })
      .filter(Boolean);
    const values = new Map(rows.map(({ label, value }) => [label.toLowerCase(), value]));
    const dimension = (wholeLabel, fractionLabel, suffix) => {
      const whole = values.get(wholeLabel);
      if (!whole) return "";
      const fraction = values.get(fractionLabel);
      return `${whole}${fraction && !/^(?:0|--)$/.test(fraction) ? ` ${fraction}` : ""}″ ${suffix}`;
    };
    const width = dimension("width", "width fraction", "W");
    const length = dimension("length", "length fraction", "L");
    const dimensionLabels = /^(?:width|width fraction|length|length fraction)$/i;
    const selections = rows
      .filter(({ label }) => !dimensionLabels.test(label))
      .map(({ label, value }) => ({
        label: label === "Header Style" ? "Header" : label === "Header Type" ? "Type" : label,
        value,
      }));
    if (width || length) selections.push({ label: "Size", value: [width, length].filter(Boolean).join(" × ") });
    const priority = [
      /^Collection$/i,
      /^Color$/i,
      /^(?:Header|Lift Type|Fold Style|Style)$/i,
      /^Type$/i,
      /^Size$/i,
      /^Installation(?: Method)?$/i,
      /^Lining$/i,
    ];
    const importantSelections = selections
      .map((selection, index) => ({
        ...selection,
        rank: priority.findIndex((pattern) => pattern.test(selection.label)),
        index,
      }))
      .filter(({ rank }) => rank >= 0)
      .sort((left, right) => left.rank - right.rank || left.index - right.index)
      .slice(0, 4);
    return `
      <div class="cz-cart-page-editor-preview-product">
        ${image ? `<img src="${escapePreviewHtml(image)}" alt="">` : ""}
        <div><strong>${escapePreviewHtml(title)}</strong><span>Current selections</span></div>
      </div>
      <div class="cz-cart-page-editor-preview-options">
        ${importantSelections.map(({ label, value }) => `<span><strong>${escapePreviewHtml(label)}:</strong> ${escapePreviewHtml(value)}</span>`).join("")}
      </div>`;
  };

  const loadProductHtml = (editUrl) => {
    if (!productHtmlCache.has(editUrl)) {
      productHtmlCache.set(
        editUrl,
        window.fetch(editUrl, { credentials: "same-origin" }).then((response) => {
          if (!response.ok) throw new Error(`Product editor request failed: ${response.status}`);
          return response.text();
        }),
      );
    }
    return productHtmlCache.get(editUrl);
  };

  const closeEditor = () => {
    window.clearInterval(pollTimer);
    window.clearTimeout(timeoutTimer);
    shell?.remove();
    shell = null;
    frame = null;
    activeEditorKey = "";
    editorReady = false;
    editorVisible = false;
    revealRequested = false;
  };

  const showError = () => {
    const status = shell?.querySelector(".cz-cart-page-editor-status");
    if (!status) return;
    status.innerHTML = '<div class="cz-cart-page-editor-loading-card cz-cart-page-editor-loading-card--error"><strong>Unable to load Edit Options.</strong><button type="button">Close</button></div>';
    status.querySelector("button")?.addEventListener("click", closeEditor, { once: true });
  };

  const openEditor = async (editUrl, cartIndex, { prewarm = false, sourceItem = null } = {}) => {
    if (!editUrl) return;
    const editorKey = `${editUrl}::${cartIndex || 0}`;

    if (shell && activeEditorKey === editorKey) {
      revealRequested = true;
      shell.classList.remove("is-prewarming");
      if (editorVisible) shell.classList.add("is-ready");
      return;
    }
    if (shell) closeEditor();

    activeEditorKey = editorKey;
    revealRequested = !prewarm;
    const preview = loadingPreview(cartIndex, sourceItem);
    shell = document.createElement("div");
    shell.className = `cz-cart-page-editor-shell${prewarm ? " is-prewarming" : ""}`;
    shell.innerHTML = `
      <div class="cz-cart-page-editor-status" role="status" aria-label="Loading Edit Options">
        <div class="cz-cart-page-editor-loading-card">
          <header><strong>Edit Options</strong><button type="button" aria-label="Close Edit Options">×</button></header>
          <div class="cz-cart-page-editor-loading-body" aria-hidden="true">
            ${preview}
            <div class="cz-cart-page-editor-loading-message">
              <span class="cz-cart-page-editor-spinner" aria-hidden="true"></span>
              <div><strong>Loading customization options…</strong><span>This usually takes a few seconds.</span></div>
            </div>
            <span class="cz-cart-page-editor-loading-line${preview ? " cz-cart-page-editor-loading-line--after-preview" : ""}"></span>
            <span class="cz-cart-page-editor-loading-grid${preview ? " cz-cart-page-editor-loading-grid--after-preview" : ""}"></span>
            <span class="cz-cart-page-editor-loading-line cz-cart-page-editor-loading-line--short"></span>
            <span class="cz-cart-page-editor-loading-grid cz-cart-page-editor-loading-grid--small"></span>
          </div>
          <span class="visually-hidden">Loading customization options. This usually takes a few seconds.</span>
        </div>
      </div>
      <iframe class="cz-cart-page-editor-frame" title="Edit Options"></iframe>`;
    document.body.append(shell);
    shell.querySelector(".cz-cart-page-editor-loading-card button")?.addEventListener("click", closeEditor, { once: true });
    frame = shell.querySelector("iframe");
    const currentShell = shell;
    const currentFrame = frame;

    try {
      const productHtml = await loadProductHtml(editUrl);
      if (shell !== currentShell) return;
      const productUrl = new URL(editUrl, window.location.origin);
      const bridge = `<base href="${productUrl.origin}/"><style>html,body{background:transparent!important}body>*{visibility:hidden!important}#ymqmodal-edit-option,#ymqmodal-edit-option *{visibility:visible!important}</style>`;

      currentFrame.addEventListener("load", () => {
        if (shell !== currentShell) return;
        const frameDocument = currentFrame.contentDocument;
        if (!frameDocument) return showError();
        let drawerOpened = false;
        let editorOpened = false;
        let modalReady = false;

        frameDocument.addEventListener("click", (event) => {
          // Product-option help links are useful on the PDP, but navigating the
          // isolated cart editor replaces it with a page that cannot be framed.
          // Keep Edit Options focused on changing selections and prevent the
          // resulting blocked iframe/scroll trap even before its CSS finishes.
          if (event.target.closest?.("#ymqmodal-edit-option a")) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          if (event.target.closest?.("#ymqmodal-edit-option .ymq-option-small-button")) savePending = true;
        }, true);

        pollTimer = window.setInterval(() => {
          if (!drawerOpened) {
            const actions = frameDocument.querySelector(
              `cart-drawer-item-actions[data-cart-index="${CSS.escape(String(cartIndex || 0))}"]`,
            );
            const cartTrigger = frameDocument.querySelector("a.cart-link");
            if (actions) {
              // Hardware and other custom product templates can render the
              // complete cart drawer without a visible header cart link. The
              // target line is enough to open YMQ's editor; do not wait for a
              // trigger that this template intentionally omits.
              drawerOpened = true;
            } else if (cartTrigger) {
              drawerOpened = true;
              cartTrigger.click();
            }
          }

          if (drawerOpened && !editorOpened) {
            const actions = frameDocument.querySelector(
              `cart-drawer-item-actions[data-cart-index="${CSS.escape(String(cartIndex || 0))}"]`,
            );
            const nativeEdit = actions?.closest(".cart-item")?.querySelector('[ymq-option-action="cart-edit"]');
            if (nativeEdit) {
              editorOpened = true;
              nativeEdit.click();
            }
          }

          const modal = frameDocument.querySelector("#ymqmodal-edit-option");
          const groupCount = modal?.querySelectorAll(".ymq-options-box").length || 0;
          const modalVisible = Boolean(modal && frameDocument.defaultView.getComputedStyle(modal).display !== "none");
          const saveButton = modal?.querySelector(".ymq-option-small-button");
          if (!editorVisible && modalVisible && groupCount > 0) {
            editorVisible = true;
            currentShell.classList.add("is-partial");
            if (revealRequested) currentShell.classList.add("is-ready");
          }
          if (editorVisible && !modalReady && saveButton) {
            saveButton.disabled = true;
            saveButton.setAttribute("aria-busy", "true");
          }
          if (!modalReady && modalVisible && groupCount > 2) {
            modalReady = true;
            editorReady = true;
            currentShell.classList.add("is-prepared");
            currentShell.classList.remove("is-partial");
            if (saveButton) {
              saveButton.disabled = false;
              saveButton.removeAttribute("aria-busy");
            }
            if (revealRequested) currentShell.classList.add("is-ready");
          }
          if (modalReady && !modalVisible) {
            if (savePending) window.location.reload();
            else closeEditor();
          }
        }, 100);
      }, { once: true });

      currentFrame.srcdoc = productHtml.replace(/<head(\s[^>]*)?>/i, (head) => `${head}${bridge}`);
      timeoutTimer = window.setTimeout(() => {
        if (shell === currentShell && !currentShell.classList.contains("is-prepared")) showError();
      }, 20000);
    } catch (error) {
      console.error("Unable to load full cart editor", error);
      showError();
    }
  };

  document.addEventListener("click", (event) => {
    const cartEdit = event.target.closest?.("[data-cart-item-edit]");
    if (cartEdit) {
      const item = cartEdit.closest(".cart-item");
      if (!item?.dataset.cartEditUrl) return;
      const actions = cartEdit.closest("cart-drawer-item-actions");
      // Simple accessories already have a complete lightweight YMQ editor in
      // the cart. Let that native card open instead of booting a full PDP.
      if (actions?.dataset.editorMode === "native") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openEditor(item.dataset.cartEditUrl, item.dataset.cartIndex, { sourceItem: item });
      return;
    }

    const nativeEdit = event.target.closest?.('[ymq-option-action="cart-edit"]');
    if (!nativeEdit) return;
    const item = nativeEdit.closest(".cart-item");
    if (!item?.dataset.cartEditUrl) return;
    const actions = item.querySelector("cart-drawer-item-actions");
    if (actions?.dataset.editorMode === "native") return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openEditor(item.dataset.cartEditUrl, item.dataset.cartIndex, { sourceItem: item });
  }, true);

  // Warm the most likely editors while the customer reviews the cart. This
  // removes the product-document download from the click-to-open path without
  // loading every large product configurator in a long cart.
  const editableItems = [...document.querySelectorAll(
    '.cart-item[data-cart-edit-url]:has(cart-drawer-item-actions[data-ymq-options="true"])',
  )];
  editableItems
    .slice(0, 3)
    .forEach((item) => loadProductHtml(item.dataset.cartEditUrl).catch(() => {}));

  // The standalone cart does not contain YMQ's complete option payload. Fully
  // prepare the first editor off-screen so the common click path only reveals
  // an already-open native modal instead of booting a product page on demand.
  const firstEditableItem = editableItems[0];
  if (firstEditableItem) {
    window.setTimeout(() => {
      openEditor(firstEditableItem.dataset.cartEditUrl, firstEditableItem.dataset.cartIndex, {
        prewarm: true,
        sourceItem: firstEditableItem,
      });
    }, 0);
  }
})();

// Keep cart-line editing compatible with YMQ's native option editor.
if (!customElements.get("cart-drawer-item-actions")) {
  customElements.define(
    "cart-drawer-item-actions",
    class CartDrawerItemActions extends HTMLElement {
      connectedCallback() {
        this.editButton = this.querySelector("[data-cart-item-edit]");
        this.editButton?.addEventListener("click", this.handleEdit);
      }

      disconnectedCallback() {
        this.editButton?.removeEventListener("click", this.handleEdit);
      }

      handleEdit = async () => {
        const item = this.closest(".cart-item");
        if (!item || !this.editButton) return;

        const openYmqEditor = () => {
          const nativeEdit = item.querySelector(
            '.ymq-option-cart-edit-wrapper [ymq-option-action="cart-edit"]',
          );
          if (!nativeEdit) return false;
          nativeEdit.click();
          window.setTimeout(() => window.czEnhanceCartEditModal?.(), 100);
          window.setTimeout(() => window.czEnhanceCartEditModal?.(), 350);
          return true;
        };

        if (openYmqEditor()) return;

        if (this.dataset.ymqOptions === "true") {
          this.editButton.setAttribute("aria-busy", "true");
          const editorReady = await new Promise((resolve) => {
            const timeout = window.setTimeout(() => {
              observer.disconnect();
              resolve(false);
            }, 3000);
            const observer = new MutationObserver(() => {
              if (!openYmqEditor()) return;
              window.clearTimeout(timeout);
              observer.disconnect();
              resolve(true);
            });
            observer.observe(item, { childList: true, subtree: true });
          });
          this.editButton.removeAttribute("aria-busy");
          if (editorReady) return;
        }

        // The top-level capture handler opens the isolated editor directly.
        // Do not navigate away when a third-party native trigger is delayed.
      };
    },
  );
}

// YMQ owns the cart-edit form and pricing. Add only the presentation helpers
// that the Liam guided product form normally supplies around those controls.
(() => {
  const modalSelector = ".ymqmodal-slide:has(.ymq-option-edit-option-content)";
  const headerNames = {
    "Double Pleats": "Double Pleat (Pinch / Tailor / French)",
    "Triple Pleats": "Triple Pleat (Pinch / Tailor / French)",
  };
  const liningNames = {
    "Unlined": "Unlined",
    "Standard Privacy Liner | 40%-50% Blackout Rate": "Light Filtering",
    "Medium Room Darkening Liner | 70%-80% Blackout Rate": "Room Darkening",
    "Thermal Heavyweight Blackout Liner | 95%-98% Blackout Rate": "Thermal",
    "Standard Blackout Liner | 100% Blackout Rate": "Blackout",
    "Waterproof Blackout Liner | 100% Blackout Rate": "Waterproof Blackout",
    "Wiva Waterproof Shower Curtain Layer | 30% Blackout Rate": "Shower Layer",
  };
  const softHeaderValues = new Set([
    "Pinch Pleat with Back Tab",
    "Tab Top",
    "Tab Top with Bow",
    "Back Tab",
    "Rod Pocket",
  ]);
  const sizeDiagramAssetKeys = {
    "Double Pleats": "pleated",
    "Triple Pleats": "pleated",
    Goblet: "pleated",
    Grommet: "grommet",
    "Pinch Pleat with Back Tab": "softPinchBackTab",
    "Tab Top": "softTabTop",
    "Tab Top with Bow": "softTabTopBow",
    "Back Tab": "softBackTab",
    "Rod Pocket": "softRodPocket",
  };

  const protectionArt = `
    <svg class="cz-cart-edit-protection-art" viewBox="0 0 150 83" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M34 9h66M39 10v54m12-54v54m12-54v54m12-54v54m12-54v54m12-54v54M34 64h66"/>
      <path d="M29 9v55m-4-51 4-4 4 4m-8 47 4 4 4-4M34 73h66m-62-4-4 4 4 4m58-8 4 4-4 4"/>
      <circle cx="119" cy="33" r="15" fill="#f8f5f1"/><path d="m112 33 5 5 9-11"/>
    </svg>`;

  const displayCaption = (group, value) => {
    const normalized = value.trim();
    if (group === "Color") return normalized.replace(/-L\d+$/i, "");
    if (group === "Header Style (Hooks included)") return headerNames[normalized] || normalized;
    if (group === "Lining") return liningNames[normalized] || normalized.split("|")[0].trim();
    if (group === "Grommet Color") return normalized.replace(/^Grommet\s+/i, "");
    return normalized;
  };

  const removeImageTooltip = (item) => {
    [
      item,
      ...item.querySelectorAll(
        "[ymq-option-tooltip-id], .ymq-option-tooltip, .ymq-option-swatches-image-container, .ymq-option-swatches-image",
      ),
    ].forEach((node) => {
      if (node._tippy && !node._tippy.state?.isDestroyed) node._tippy.destroy();
      node.classList.remove("ymq-option-tooltip");
      node.removeAttribute("ymq-option-tooltip-id");
      node.removeAttribute("title");
    });
  };

  const enhanceImageOptions = (modal) => {
    modal.querySelectorAll('[ymq-option-type="5"]').forEach((box) => {
      const group = box.getAttribute("ymq-option-label") || "";
      box.querySelectorAll(".ymq-option-label").forEach((label) => {
        removeImageTooltip(label.closest(".ymq-option-value-item") || label);
        const input = label.querySelector('input:not([type="hidden"])');
        if (!input) return;
        const captionText = displayCaption(group, input.value || "");
        let caption = label.querySelector(":scope > .cz-cart-edit-caption");
        if (!captionText) {
          caption?.remove();
          return;
        }
        if (!caption) {
          caption = document.createElement("span");
          caption.className = "cz-cart-edit-caption";
          label.append(caption);
        }
        if (caption.textContent !== captionText) caption.textContent = captionText;
      });
    });
  };

  const enhanceHeaderHierarchy = (modal) => {
    const box = modal.querySelector('[ymq-option-label="Header Style (Hooks included)"]');
    const grid = box?.querySelector(":scope .ymq-option-value-info");
    if (!box || !grid) return;

    const softItems = [...grid.querySelectorAll(":scope > .ymq-option-value-item")].filter((item) => {
      const input = item.querySelector('input:not([type="hidden"])');
      return input && softHeaderValues.has((input.value || "").trim());
    });
    if (!softItems.length) return;

    softItems.forEach((item) => item.classList.add("cz-cart-edit-soft-choice"));

    let softCard = grid.querySelector(":scope > .cz-cart-edit-soft-card");
    if (!softCard) {
      softCard = document.createElement("button");
      softCard.type = "button";
      softCard.className = "cz-cart-edit-soft-card";
      softCard.setAttribute("aria-controls", "cz-cart-edit-soft-header-types");
      grid.insertBefore(softCard, softItems[0]);
    }
    let softCardImage = softCard.querySelector(":scope > .cz-cart-edit-soft-card-image");
    if (!softCardImage) {
      const sourceImage = softItems[0].querySelector(".ymq-option-swatches-image");
      if (sourceImage) {
        softCardImage = sourceImage.cloneNode(false);
        softCardImage.className = "cz-cart-edit-soft-card-image";
        softCardImage.alt = "Soft Top";
        softCard.prepend(softCardImage);
      }
    }
    let softCardLabel = softCard.querySelector(":scope > .cz-cart-edit-soft-card-label");
    if (!softCardLabel) {
      softCardLabel = document.createElement("span");
      softCardLabel.className = "cz-cart-edit-soft-card-label";
      softCardLabel.textContent = "Soft Top";
      softCard.append(softCardLabel);
    }

    let heading = grid.querySelector(":scope > .cz-cart-edit-soft-heading");
    if (!heading) {
      heading = document.createElement("h4");
      heading.id = "cz-cart-edit-soft-header-types";
      heading.className = "cz-cart-edit-soft-heading";
      heading.textContent = "Header Type";
      grid.insertBefore(heading, softItems[0]);
    }

    const hasSelectedSoftType = softItems.some((item) => item.querySelector("input")?.checked);
    box.classList.toggle("cz-cart-edit-soft-selected", hasSelectedSoftType);
    softCard.setAttribute("aria-pressed", hasSelectedSoftType ? "true" : "false");
    softCard.setAttribute(
      "aria-expanded",
      hasSelectedSoftType || box.classList.contains("cz-cart-edit-soft-open") ? "true" : "false",
    );
  };

  const optionValue = (modal, label) => {
    const box = modal.querySelector(`[ymq-option-label="${label}"]`);
    const select = box?.querySelector("select:not(:disabled)");
    if (select) return (select.value || "").trim();
    const checked = box?.querySelector("input:checked:not(:disabled)");
    if (checked) return (checked.value || "").trim();
    const input = box?.querySelector('input:not([type="hidden"]):not(:disabled)');
    return (input?.value || "").trim();
  };

  const formattedSizeValue = (modal, wholeLabel, fractionLabel) => {
    const whole = optionValue(modal, wholeLabel);
    const fraction = optionValue(modal, fractionLabel);
    if (!whole) return "";
    return `${whole}${fraction && fraction !== "0" ? ` ${fraction}` : ""}″`;
  };

  const syncSizeDiagram = (modal, section) => {
    const style = optionValue(modal, "Header Style (Hooks included)");
    const assetKey = sizeDiagramAssetKeys[style];
    const source = assetKey && window.czCartEditAssets?.[assetKey];
    const visual = section.querySelector(".cz-cart-edit-size-visual");
    const art = section.querySelector(".cz-cart-edit-size-diagram-art");
    const image = art?.querySelector("img");
    if (!visual || !art || !image) return;

    visual.hidden = !source;
    if (!source) return;
    if (image.getAttribute("src") !== source) image.src = source;

    const flatReference = style === "Grommet" || softHeaderValues.has(style);
    const kind = flatReference ? "flat-reference" : "pleated";
    art.dataset.czDiagramKind = kind;
    art.dataset.czDiagramVariant = style;
    image.alt = flatReference
      ? `${style} measurement diagram showing Order Width and Order Length for one panel; Fabric Flat Width is reference only.`
      : `${style} finished panel measurement diagram showing Width and Length.`;

    const width = formattedSizeValue(modal, "Single Panel Order Width (inch)", "Width Fraction (optional)");
    const length = formattedSizeValue(modal, "Single Panel Order Length (inch)", "Length Fraction (optional)");
    const widthLabel = art.querySelector("[data-cz-cart-diagram-width]");
    const lengthLabel = art.querySelector("[data-cz-cart-diagram-length]");
    const lengthName = style === "Rod Pocket" ? "Order Height" : kind === "flat-reference" ? "Order Length" : "Length";
    const widthName = kind === "flat-reference" ? "Order Width" : "Width";
    widthLabel.hidden = !width;
    lengthLabel.hidden = !length;
    if (width) widthLabel.textContent = kind === "flat-reference" ? `${widthName}\n${width}` : `${widthName}: ${width}`;
    if (length) lengthLabel.textContent = `${lengthName}: ${length}`;
  };

  const enhanceSize = (modal) => {
    const optionRoot = modal.querySelector(".ymq-option-edit-option-content > ymq-option");
    const fieldLabels = [
      "Single Panel Order Width (inch)",
      "Width Fraction (optional)",
      "Single Panel Order Length (inch)",
      "Length Fraction (optional)",
    ];
    const fields = fieldLabels.map((label) => modal.querySelector(`[ymq-option-label="${label}"]`));
    if (!optionRoot || fields.some((field) => !field)) return;

    let section = optionRoot.querySelector(".cz-cart-edit-size-intro");
    if (!section) {
      section = document.createElement("section");
      section.className = "cz-cart-edit-size-intro";
      section.innerHTML = `
        <h3>Dimensions</h3>
        <div class="cz-cart-edit-size-advice">
          <p>Width: rod/track coverage per panel · Length: finished panel top to hem.</p>
          <p>Enter each panel's size. For a pair, set quantity to 2.</p>
        </div>
        <div class="cz-cart-edit-size-layout">
          <figure class="cz-cart-edit-size-visual">
            <span class="cz-cart-edit-size-diagram-art">
              <img alt="" loading="lazy" decoding="async">
              <span class="cz-cart-edit-size-diagram-label cz-cart-edit-size-diagram-label--width" data-cz-cart-diagram-width hidden></span>
              <span class="cz-cart-edit-size-diagram-label cz-cart-edit-size-diagram-label--length" data-cz-cart-diagram-length hidden></span>
            </span>
          </figure>
          <div class="cz-cart-edit-size-fields"></div>
        </div>`;
      optionRoot.append(section);
    }

    const shortLabels = ["Width (in)", "Extra inches", "Length (in)", "Extra inches"];
    const fieldGrid = section.querySelector(".cz-cart-edit-size-fields");
    fields.forEach((field, index) => {
      if (field.parentElement !== fieldGrid) fieldGrid.append(field);
      const label = field.querySelector(".ymq_lable");
      if (label && label.textContent.trim() !== shortLabels[index]) label.textContent = shortLabels[index];
    });
    syncSizeDiagram(modal, section);
  };

  const enhanceProtection = (modal, { compact = false } = {}) => {
    const box = modal.querySelector('[ymq-option-label="Fit Protection (Optional)"]');
    if (!box) return;
    const title = box.querySelector(".ymq_lable");
    if (title && title.textContent !== "Size Protection") title.textContent = "Size Protection";

    if (compact) {
      box.classList.add("cz-cart-edit-protection-compact");
      box.querySelectorAll(".cz-cart-edit-protection-art, .cz-cart-edit-protection-caption").forEach((node) => node.remove());
      return;
    }

    const descriptions = {
      "Add protection": "One size remake if your measurements are off.",
      "No thanks": "Continue without this optional service.",
    };
    Object.entries(descriptions).forEach(([value, description]) => {
      const input = [...box.querySelectorAll("input")].find((candidate) => candidate.value === value);
      const content = input?.nextElementSibling;
      if (!content) return;
      if (value === "Add protection" && !content.querySelector(".cz-cart-edit-protection-art")) {
        content.insertAdjacentHTML("afterbegin", protectionArt);
      }
      let caption = content.querySelector(".cz-cart-edit-protection-caption");
      if (!caption) {
        caption = document.createElement("span");
        caption.className = "cz-cart-edit-protection-caption";
        content.append(caption);
      }
      if (caption.textContent !== description) caption.textContent = description;
    });
  };

  const enhanceShade = (modal) => {
    const hiddenInfoLabels = new Set([
      "订购",
      "声控",
      "单通",
      "多通",
      "解释通道",
      "无衬里解释",
      "inside测量指示",
      "outside测量指示",
      "包边解释",
      "保障",
    ]);

    modal.querySelectorAll(".ymq-options-box").forEach((box) => {
      const label = (box.getAttribute("ymq-option-label") || "").trim();
      const isCopyOnly = box.getAttribute("ymq-option-type") === "11"
        && !box.querySelector('input:not([type="hidden"]), select, textarea');
      box.classList.toggle("cz-cart-edit-shade-info", hiddenInfoLabels.has(label) || isCopyOnly);
    });
    enhanceShadeSize(modal);
    enhanceProtection(modal, { compact: true });
  };

  const enhanceShadeSize = (modal) => {
    const optionRoot = modal.querySelector(".ymq-option-edit-option-content > ymq-option");
    const installation = modal.querySelector('[ymq-option-label="Installation Method"]');
    const liftType = modal.querySelector('[ymq-option-label="Lift Type"]');
    const fields = [...modal.querySelectorAll(".ymq-options-box")].filter((box) => {
      const label = box.getAttribute("ymq-option-label") || "";
      return /^(?:Shade Width \(inch|Width Fraction|Shade Length \(inch|Length Fraction)/.test(label);
    });
    if (!optionRoot || !installation || !fields.length) return;

    // Match the product-page flow: Style & Lift is completed before Mount &
    // Size. YMQ's cart editor can return Installation ahead of Fold Style, so
    // restore the storefront order before placing the conditional size guide.
    if (liftType && installation.previousElementSibling !== liftType) {
      liftType.after(installation);
    }

    let section = optionRoot.querySelector(".cz-cart-edit-shade-size");
    if (!section) {
      section = document.createElement("section");
      section.className = "cz-cart-edit-size-intro cz-cart-edit-shade-size";
      section.innerHTML = `
        <h3>Size</h3>
        <div class="cz-cart-edit-size-advice">
          <p>Choose Inside Mount or Outside Mount to see the matching finished-size guidance.</p>
        </div>
        <div class="cz-cart-edit-size-layout">
          <figure class="cz-cart-edit-size-visual">
            <span class="cz-cart-edit-size-diagram-art">
              <img alt="" loading="lazy" decoding="async">
            </span>
          </figure>
          <div class="cz-cart-edit-size-fields"></div>
        </div>`;
    }
    if (section.previousElementSibling !== installation) installation.after(section);

    const fieldGrid = section.querySelector(".cz-cart-edit-size-fields");
    fields.forEach((field) => {
      if (field.parentElement !== fieldGrid) fieldGrid.append(field);
      const label = field.getAttribute("ymq-option-label") || "";
      const title = field.querySelector(".ymq_lable");
      if (!title) return;
      const shortLabel = label.startsWith("Shade Width")
        ? "Width (in)"
        : label.startsWith("Shade Length")
          ? "Length (in)"
          : "Extra inches";
      if (title.textContent.trim() !== shortLabel) title.textContent = shortLabel;
    });

    const mount = optionValue(modal, "Installation Method");
    const outside = /Outside/i.test(mount);
    const hasMount = /^(?:Inside|Outside) Mount$/i.test(mount);
    const visual = section.querySelector(".cz-cart-edit-size-visual");
    const image = section.querySelector(".cz-cart-edit-size-diagram-art img");
    const source = !hasMount
      ? ""
      : outside
        ? window.czCartEditAssets?.romanOutside
        : window.czCartEditAssets?.romanInside;
    if (visual) visual.hidden = !hasMount;
    if (!hasMount) image?.removeAttribute("src");
    if (image && source && image.getAttribute("src") !== source) image.src = source;
    if (image) image.alt = hasMount
      ? `${outside ? "Outside" : "Inside"} mount Roman shade order width and order length diagram`
      : "";
  };

  const enhanceModal = (modal) => {
    if (!modal.querySelector(".ymq-option-edit-option-content")) return;
    const isDrapery = Boolean(modal.querySelector(
      '[ymq-option-label="Header Style (Hooks included)"]',
    ));
    const isShade = !isDrapery && Boolean(modal.querySelector(
      '[ymq-option-label^="Shade Width (inch"]',
    ));
    modal.dataset.czCartEditKind = isDrapery ? "drapery" : isShade ? "shade" : "native";
    enhanceImageOptions(modal);
    if (isDrapery) {
      enhanceHeaderHierarchy(modal);
      enhanceSize(modal);
      enhanceProtection(modal);
    } else if (isShade) {
      enhanceShade(modal);
    }
    modal.dataset.czCartEditPresentation = "ready";
  };

  const enhanceAll = () => document.querySelectorAll(modalSelector).forEach(enhanceModal);
  let enhancementFrame = 0;
  const queueEnhancement = () => {
    if (enhancementFrame) return;
    enhancementFrame = window.requestAnimationFrame(() => {
      enhancementFrame = 0;
      enhanceAll();
    });
  };

  window.czEnhanceCartEditModal = enhanceAll;
  const modalObserver = new MutationObserver((mutations) => {
    const editorAdded = mutations.some(({ addedNodes }) =>
      [...addedNodes].some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.(
            ".ymqmodal-slide, .ymq-option-edit-option-content, ymq-option, .ymq-options-box, .ymq-option-value-item",
          ) ||
            node.querySelector?.(
              ".ymqmodal-slide, .ymq-option-edit-option-content, ymq-option, .ymq-options-box, .ymq-option-value-item",
            )),
      ),
    );
    if (!editorAdded) return;
    queueEnhancement();
    window.setTimeout(enhanceAll, 150);
    window.setTimeout(enhanceAll, 350);
  });
  if (document.body) modalObserver.observe(document.body, { childList: true, subtree: true });
  document.addEventListener("click", (event) => {
    const softCard = event.target.closest?.(`${modalSelector} .cz-cart-edit-soft-card`);
    if (!softCard) return;
    event.preventDefault();
    event.stopPropagation();
    const headerBox = softCard.closest('[ymq-option-label="Header Style (Hooks included)"]');
    headerBox?.classList.add("cz-cart-edit-soft-open");
    softCard.setAttribute("aria-expanded", "true");
  });
  document.addEventListener("change", (event) => {
    if (!event.target.closest?.(modalSelector)) return;
    const headerBox = event.target.closest?.('[ymq-option-label="Header Style (Hooks included)"]');
    if (headerBox && !softHeaderValues.has((event.target.value || "").trim())) {
      headerBox.classList.remove("cz-cart-edit-soft-open");
    }
    queueEnhancement();
    window.setTimeout(enhanceAll, 150);
  });
  enhanceAll();
})();
